import type { FC, PropsWithChildren } from "react";
import { useParams } from "react-router-dom";

import TrialItemIdentityContext from "src/contexts/data/TrialItemIdentityContext";
import useTrialItemIdentityById from "src/hooks/data/load/useTrialItemIdentityById";

/**
 * Loads trial item identity from the matching route parameter for descendants.
 *
 * Preconditions: Render within the router that supplies the route parameters.
 * Postconditions: An absent or empty parameter becomes undefined; otherwise base-10 parseInt
 * supplies the ID, accepting numeric prefixes and producing NaN for unparseable text.
 * Loading and errors remain in context; this wrapper does not gate children or enforce access.
 * Assumptions: The loader handles request state and the server enforces authorization.
 */
export const RouteTrialItemIdentityProvider: FC<PropsWithChildren> = ({ children }) => {
    const { trialItemId } = useParams<{ trialItemId: string }>();
    const context = useTrialItemIdentityById(
        trialItemId ? Number.parseInt(trialItemId, 10) : undefined
    );

    return (
        <TrialItemIdentityContext.Provider value={context}>
            {children}
        </TrialItemIdentityContext.Provider>
    );
};
